// MO4.1.3
import React from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import type {
  PapiDeviation,
  PapiDeviationTier,
  PapiUser,
} from '@wix/da-papi-types';
import Flex from '@wix/da-ds/pkg/Flex';
import UserIdentity from '../../../../User/Identity';
import type { LayoutStyleProperties } from '../../../../types';
import TierPricePerMonth from '../../../Helper/TierPricePerMonth';
import TierPriceWithBadge from '../../../Helper/TierPriceWithBadge';
import TierStatisticsBar from '../../../Helper/StatisticsBar/TierStatisticsBar';
import DeviationDumbView from '../../../Dumbo/EditorDumbo';
import UnknownDumbo from '../../../Dumbo/Unknown';
import Minimal from './Minimal';

import s from './TierPromotionCardEditorDumb.scss';

const RATIO = 1.84;
const DESCRIPTION_CUTOFF_HEIGHT = 250;
const SUPER_NARROW_WIDTH = 300;
const STATS_CUTOFF_HEIGHT = 195;
const MINIMAL_VIEW_HEIGHT = 120;
const DUMBO_MAX_WIDTH_RATIO = 0.33;

function getDimensions(width?: number, height?: number) {
  if (width) {
    return { width, height: width / RATIO };
  } else if (height) {
    return { width: height * RATIO, height };
  }
}

function getDumboHeight(cardHeight: number) {
  // spacings =  4 * 16px
  // footer block = 32px
  // borders = 2px
  return cardHeight - 4 * 16 - 32 - 2;
}

export interface Props {
  title?: string;
  description?: string;
  author?: PapiUser;
  showAuthor?: boolean;
  coverImage?: PapiDeviation;
  showMatureCover?: boolean;
  stats?: PapiDeviationTier['stats'];
  price?: string;
  state?: 'draft' | 'active' | 'pending_deletion' | 'deleted';
  width?: number;
  height?: number;
  /** used in grids for dynamic positioning to not wrap every item in a container
   * do not abuse for anything else or you will get fire ants in your socks
   */
  style?: LayoutStyleProperties;
  showEmptyPlaceholders?: boolean;
  className?: string;
  hotspot?: React.ReactNode;
  ctaButton?: React.ReactNode;
  onClick?: () => void;
  innerRef?: (node: HTMLElement | null) => void;
}

const TierPromotionCardDumb: React.FC<Props> = ({
  title,
  description,
  author,
  showAuthor = true,
  stats,
  price,
  coverImage,
  showMatureCover,
  hotspot,
  ctaButton,
  width,
  height,
  style,
  showEmptyPlaceholders,
  state = 'active',
  className,
  onClick,
  innerRef,
}) => {
  const { t } = useTranslation();

  const dimensions = getDimensions(width, height);
  if (!dimensions) {
    return null;
  }

  if (dimensions.height <= MINIMAL_VIEW_HEIGHT) {
    return (
      <Minimal
        deviation={coverImage}
        className={classnames(s['root'], className)}
        onClick={onClick}
        {...dimensions}
        style={style}
      />
    );
  }

  const showDescription =
    description && dimensions.height >= DESCRIPTION_CUTOFF_HEIGHT;
  const dumboWidth = dimensions.width * DUMBO_MAX_WIDTH_RATIO;
  const dumboHeight = getDumboHeight(dimensions.height);
  const isNarrow = !!width && width < SUPER_NARROW_WIDTH;
  return (
    <div
      className={classnames(s['root'], isNarrow && s['narrow'], className)}
      style={{ ...dimensions, ...style }}
      onClick={onClick}
      ref={innerRef}
    >
      <Flex
        className={s['details-and-thumb']}
        style={{ maxHeight: isNarrow ? '100%' : dumboHeight }}
      >
        <div className={s['card-details']}>
          {showAuthor && author && (
            <UserIdentity
              user={author}
              size="small"
              className={s['identity']}
            />
          )}
          {showEmptyPlaceholders && !title && (
            <div
              className={classnames(s['placeholder'], s['placeholder-title'])}
            />
          )}
          {title && <div className={s['title']}>{title}</div>}
          {dimensions.height >= STATS_CUTOFF_HEIGHT && (
            <TierStatisticsBar className={s['statistics']} stats={stats} />
          )}
          {showDescription && (
            <div className={s['description']}>{description}</div>
          )}
          {hotspot}
        </div>
        <div className={s['dumbo-wrapper']}>
          {coverImage ? (
            <DeviationDumbView
              className={s['dumbo']}
              deviation={coverImage}
              width={dumboWidth}
              height={dumboHeight}
              showMatureContent={showMatureCover}
            />
          ) : showEmptyPlaceholders ? (
            <div
              className={s['placeholder-cover']}
              style={{ width: dumboWidth, height: dumboHeight }}
            />
          ) : (
            <UnknownDumbo
              className={s['dumbo']}
              width={dumboWidth}
              height={dumboHeight}
            />
          )}
          {state !== 'active' && (
            <div className={classnames(s['state-tag'], s[state])}>
              {t(`deviationview.tierCard.state.${state}`)}
            </div>
          )}
        </div>
      </Flex>

      <hr className={s['divider']} />

      <Flex alignItems="center" className={s['price-wrapper']}>
        {price ? (
          <TierPricePerMonth className={s['price']} price={price} />
        ) : (
          <TierPriceWithBadge
            className={s['price']}
            price={price}
            showPlaceholder={showEmptyPlaceholders}
          />
        )}
        {ctaButton}
      </Flex>
    </div>
  );
};
TierPromotionCardDumb.displayName = 'TierPromotionCardDumb';

export default TierPromotionCardDumb;
